import { useMemo } from 'react'
import {
  apiSaveProject,
  apiUpdateProject,
  apiSavePosition,
  apiUpdatePosition,
  apiApproveTruthSuggestion,
  apiBatchResolveTruthSuggestions,
  apiDismissTruthSuggestion,
  apiInferTruthSuggestions,
  apiFetchTruthReviewQueue,
} from '../studioApi.js'
import { useStudioState } from '../contexts/StudioStateContext'
import { useTruth } from '../contexts/TruthContext'
import type {
  BootstrapPayload,
  Position,
  Project,
  StateSetter,
  TruthReviewQueue,
  TruthSuggestion,
  TruthSuggestionResponse,
} from '../types'

export interface TruthHandlersApi {
  saveProject: (project: Project) => Promise<Project>
  updateProject: (id: string, updates: Partial<Project>) => Promise<Project>
  savePosition: (position: Position) => Promise<Position>
  updatePosition: (id: string, updates: Partial<Position>) => Promise<Position>
  approveSuggestion: (
    id: string,
    decision: unknown
  ) => Promise<TruthSuggestionResponse>
  batchResolveSuggestions: (
    decisions: unknown[]
  ) => Promise<TruthSuggestionResponse>
  dismissSuggestion: (id: string) => Promise<TruthSuggestionResponse>
  inferSuggestions: () => Promise<TruthSuggestionResponse>
  fetchReviewQueue: () => Promise<TruthReviewQueue>
}

const DEFAULT_API: TruthHandlersApi = Object.freeze({
  saveProject: apiSaveProject,
  updateProject: apiUpdateProject,
  savePosition: apiSavePosition,
  updatePosition: apiUpdatePosition,
  approveSuggestion: apiApproveTruthSuggestion,
  batchResolveSuggestions: apiBatchResolveTruthSuggestions,
  dismissSuggestion: apiDismissTruthSuggestion,
  inferSuggestions: apiInferTruthSuggestions,
  fetchReviewQueue: apiFetchTruthReviewQueue,
})

export interface TruthHandlersDeps {
  setProjects: StateSetter<Project[]>
  setPositions: StateSetter<Position[]>
  setTruthSuggestions: StateSetter<TruthSuggestion[]>
  applyBootstrap: (payload: BootstrapPayload) => void
  applyReviewQueue: (payload?: TruthReviewQueue | null) => void
  onToast?: (message: string) => void
  api?: TruthHandlersApi
}

export interface TruthHandlers {
  handleSaveProject: (project: Project) => Promise<Project>
  handleUpdateTruthProject: (
    id: string,
    updates: Partial<Project>
  ) => Promise<Project>
  handleSavePosition: (position: Position) => Promise<Position>
  handleUpdateTruthPosition: (
    id: string,
    updates: Partial<Position>
  ) => Promise<Position>
  handleApproveTruthSuggestion: (id: string, decision: unknown) => Promise<void>
  handleBatchResolveTruthSuggestions: (decisions: unknown[]) => Promise<void>
  handleDismissTruthSuggestion: (id: string) => Promise<void>
  handleInferTruthSuggestions: () => Promise<void>
}

const noopToast = (_message: string): void => {}

// Pure factory for truth handlers (projects, positions, suggestions).
//
// `applyBootstrap` is passed in rather than reconstructed because it
// touches every bootstrap-shaped slot across multiple contexts. Phase C
// keeps it as an injected callback; promoting it to its own context is
// a future refactor when more handlers need it.
export function createTruthHandlers(deps: TruthHandlersDeps): TruthHandlers {
  const {
    setProjects,
    setPositions,
    setTruthSuggestions,
    applyBootstrap,
    applyReviewQueue,
    onToast = noopToast,
    api = DEFAULT_API,
  } = deps

  async function handleSaveProject(project: Project): Promise<Project> {
    const savedProject = await api.saveProject(project)
    setProjects((prev) => [
      savedProject,
      ...prev.filter((item) => item.id !== savedProject.id),
    ])
    return savedProject
  }

  async function handleUpdateTruthProject(
    id: string,
    updates: Partial<Project>
  ): Promise<Project> {
    const project = await api.updateProject(id, updates)
    setProjects((prev) => prev.map((item) => (item.id === id ? project : item)))
    return project
  }

  async function handleSavePosition(position: Position): Promise<Position> {
    const savedPosition = await api.savePosition(position)
    setPositions((prev) => [
      savedPosition,
      ...prev.filter((item) => item.id !== savedPosition.id),
    ])
    return savedPosition
  }

  async function handleUpdateTruthPosition(
    id: string,
    updates: Partial<Position>
  ): Promise<Position> {
    const position = await api.updatePosition(id, updates)
    setPositions((prev) =>
      prev.map((item) => (item.id === id ? position : item))
    )
    return position
  }

  async function handleApproveTruthSuggestion(
    id: string,
    decision: unknown
  ): Promise<void> {
    const data = await api.approveSuggestion(id, decision)
    if (data?.bootstrap) applyBootstrap(data.bootstrap)
    applyReviewQueue(await api.fetchReviewQueue())
    onToast('Truth suggestion applied')
  }

  async function handleBatchResolveTruthSuggestions(
    decisions: unknown[]
  ): Promise<void> {
    const data = await api.batchResolveSuggestions(decisions)
    if (data?.bootstrap) applyBootstrap(data.bootstrap)
    if (data?.reviewQueue) {
      applyReviewQueue(data.reviewQueue)
    } else {
      applyReviewQueue(await api.fetchReviewQueue())
    }
    onToast('Truth suggestions applied')
  }

  async function handleDismissTruthSuggestion(id: string): Promise<void> {
    const data = await api.dismissSuggestion(id)
    setTruthSuggestions((prev) =>
      prev.map((item) =>
        item.id === id && data.suggestion ? data.suggestion : item
      )
    )
    applyReviewQueue(await api.fetchReviewQueue())
    onToast('Truth suggestion dismissed')
  }

  async function handleInferTruthSuggestions(): Promise<void> {
    const data = await api.inferSuggestions()
    setTruthSuggestions(data?.suggestions || [])
    if (data?.reviewQueue) {
      applyReviewQueue(data.reviewQueue)
    } else {
      applyReviewQueue(await api.fetchReviewQueue())
    }
    onToast('Truth suggestions refreshed')
  }

  return {
    handleSaveProject,
    handleUpdateTruthProject,
    handleSavePosition,
    handleUpdateTruthPosition,
    handleApproveTruthSuggestion,
    handleBatchResolveTruthSuggestions,
    handleDismissTruthSuggestion,
    handleInferTruthSuggestions,
  }
}

export interface UseTruthHandlersOptions {
  applyBootstrap: (payload: BootstrapPayload) => void
  onToast?: (message: string) => void
}

export function useTruthHandlers(
  options: UseTruthHandlersOptions
): TruthHandlers {
  const { applyBootstrap, onToast } = options
  const { setProjects, setPositions } = useStudioState()
  const { setSuggestions, applyReviewQueue } = useTruth()

  return useMemo(
    () =>
      createTruthHandlers({
        setProjects,
        setPositions,
        setTruthSuggestions: setSuggestions,
        applyBootstrap,
        applyReviewQueue,
        onToast,
      }),
    [
      setProjects,
      setPositions,
      setSuggestions,
      applyReviewQueue,
      applyBootstrap,
      onToast,
    ]
  )
}

export const __test__ = { DEFAULT_API }
